import React from "react";
import { Link } from "react-router-dom";
import { CornerDownRight, LayoutDashboard } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

/**
 * Breadcrumb — a path you walked into. Ancestors recede; the last step
 * is the floor you landed on. items: [{ label, to, icon? }]
 */
export function Breadcrumb({ items = [] }) {
  const t = useDashTheme();
  const trail = items.filter(Boolean);
  if (!trail.length) return null;
  const depth = trail.length;
  const ink = t.isDark ? "#E8EDF5" : "#1B2433";
  const inkFg = t.isDark ? "#1B2433" : "#fff";

  return (
    <nav
      aria-label="Breadcrumb"
      style={{
        display: "inline-flex", alignItems: "center", gap: 0,
        maxWidth: "100%", minWidth: 0, minHeight: 36,
        padding: "4px 4px 4px 12px",
        background: t.c.surface, border: `1px solid ${t.c.border}`,
        borderRadius: 12, fontFamily: t.font.family,
      }}
    >
      <span aria-hidden="true" title={`${depth} deep`} style={{
        display: "inline-flex", alignItems: "center", gap: 3, marginRight: 10, flexShrink: 0,
      }}>
        {trail.map((_, i) => (
          <i key={i} style={{
            display: "block",
            width: i === depth - 1 ? 12 : 5, height: 5, borderRadius: 99,
            background: i === depth - 1 ? ink : t.c.border,
          }} />
        ))}
      </span>

      {trail.map((item, i) => {
        const last = i === trail.length - 1;
        const ico = item.icon || (i === 0 ? <LayoutDashboard size={13} strokeWidth={2.2} /> : null);

        if (last) {
          return (
            <React.Fragment key={`${item.label}-${i}`}>
              {i > 0 ? (
                <CornerDownRight size={13} strokeWidth={2.2} aria-hidden="true"
                  style={{ color: t.c.textTertiary, flexShrink: 0, margin: "0 8px" }} />
              ) : null}
              <span aria-current="page" style={{
                display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0, maxWidth: 280,
                padding: "6px 11px", borderRadius: 9,
                background: ink, color: inkFg,
                fontSize: 13, fontWeight: 700, letterSpacing: "-0.2px",
              }}>
                {ico ? <span style={{ display: "grid", flexShrink: 0, opacity: 0.85 }}>{ico}</span> : null}
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.label}>
                  {item.label}
                </span>
              </span>
            </React.Fragment>
          );
        }

        const body = (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, maxWidth: 160,
            fontSize: 12.5, fontWeight: 550, color: t.c.textTertiary, letterSpacing: "-0.1px",
          }}>
            {ico ? <span style={{ display: "grid", flexShrink: 0 }}>{ico}</span> : null}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.label}>
              {item.label}
            </span>
          </span>
        );

        return (
          <React.Fragment key={`${item.label}-${i}`}>
            {i > 0 ? (
              <span aria-hidden="true" style={{ color: t.c.border, margin: "0 8px", fontSize: 13, flexShrink: 0 }}>›</span>
            ) : null}
            {item.to ? (
              <Link
                to={item.to}
                style={{ textDecoration: "none", color: "inherit", minWidth: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.color = t.c.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "inherit"; }}
              >
                {body}
              </Link>
            ) : (
              <span style={{ minWidth: 0 }}>{body}</span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default Breadcrumb;
